import { useState } from 'react';

import {
  Box,
  Button,
  Group,
  List,
  Popover,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';

import {
  IconArrowsSort,
  IconAsterisk,
  IconEyeglass,
  IconSchool,
  IconSelector,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Role } from '../../../shared/entities/Access';

export function RoleSelect({
  required,
  value,
  onChange,
  variant = 'default',
}: {
  required?: boolean;
  value: Role;
  onChange: (value: Role) => void;
  variant?: 'default' | 'filled';
}) {
  const { t } = useTranslation(['workspaceSettings', 'entities']);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Stack spacing="02">
      <Group noWrap align="center" spacing="02">
        <Text variant="sm" fw={500}>
          {t('roleSelect.label')}
        </Text>
        {required && (
          <ThemeIcon
            variant="light"
            size="04"
            sx={theme => ({
              borderRadius: theme.radius.xl,
              backgroundColor: theme.colors.primary[1],
              color: theme.colors.primary[6],
            })}
          >
            <IconAsterisk size={8} stroke={3} />
          </ThemeIcon>
        )}
      </Group>

      <Popover
        width="target"
        position="bottom"
        withinPortal
        styles={theme => ({
          dropdown: {
            padding: theme.spacing['02'],
            borderRadius: theme.spacing['02'],
            border: `1px solid ${theme.colors.gray[2]}`,
          },
        })}
        onClose={() => setIsFocused(false)}
        onOpen={() => setIsFocused(true)}
      >
        <Popover.Target>
          <Button
            variant="outline"
            styles={theme => ({
              root: {
                width: '100%',
                color: theme.colors.gray[9],
                border: `2px solid transparent`,
                borderColor:
                  variant === 'filled' ? 'transparent' : theme.colors.gray[2],
                '&:hover': {
                  backgroundColor:
                    variant === 'filled' ? theme.colors.gray[1] : 'inherit',
                  borderColor: isFocused
                    ? theme.colors.primary[5]
                    : variant === 'filled'
                      ? theme.colors.gray[1]
                      : theme.colors.gray[3],
                },
                '&:focus': { borderColor: theme.colors.primary[5] },
              },
              label: {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              },
            })}
            sx={theme => ({
              '&:active': { transform: 'initial' },
              borderRadius: theme.spacing['02'],
              background: variant === 'filled' ? theme.colors.gray[0] : 'white',
              fontSize: theme.fontSizes.sm,
              fontWeight: 400,
              padding: `${theme.spacing['02']} ${theme.spacing['03']}`,
            })}
          >
            <Text variant="sm" fw={400}>
              {t(`entities:role.${value}`)}
            </Text>
            <IconSelector size={16} />
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          {Object.values(Role).map(role => (
            <RoleItem
              key={role}
              role={role}
              onClick={() => onChange(role)}
              isSelected={value === role}
            />
          ))}
        </Popover.Dropdown>
      </Popover>
    </Stack>
  );
}

function RoleItem({
  role,
  onClick,
  isSelected,
}: {
  role: Role;
  onClick: () => void;
  isSelected: boolean;
}) {
  const { t } = useTranslation(['workspaceSettings', 'entities']);

  const descriptionItems = t(`roleSelect.description.${role}`, {
    returnObjects: true,
  });

  return (
    <Box
      p="02"
      sx={theme => ({
        borderRadius: theme.radius.sm,
        backgroundColor: isSelected ? theme.colors.gray[1] : 'transparent',
        '&:hover': {
          backgroundColor: isSelected
            ? theme.colors.gray[1]
            : theme.colors.gray[0],
          cursor: 'pointer',
        },
      })}
      onClick={onClick}
    >
      <Group noWrap align="center" spacing="02">
        {ICONS[role]}
        <Text variant="sm" fw={400}>
          {t(`entities:role.${role}`)}
        </Text>
      </Group>

      <List
        styles={theme => ({
          item: {
            '&::marker': { color: theme.colors.gray[6] },
            gap: theme.spacing['01'],
            paddingLeft: theme.spacing['05'],
            lineHeight: '18px',
            margin: 0 + ' !important',
          },
        })}
      >
        {descriptionItems.map(item => (
          <List.Item key={item}>
            <Text variant="xs" fw={400} c="gray.6">
              {item}
            </Text>
          </List.Item>
        ))}
      </List>
    </Box>
  );
}

const ICONS = {
  [Role.ADMINISTRATOR]: <IconSchool size={16} />,
  [Role.DECISION_MAKER]: <IconArrowsSort size={16} />,
  [Role.CONTRIBUTOR]: <IconEyeglass size={16} />,
};
